import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Settings, Shield, Users, Crown, Brain, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface SmartAvailabilitySyncConfig {
  enabled: boolean;
  max_connections_free: number;
  max_connections_pro: number;
}

interface HostIntelligenceConfig {
  enabled: boolean;
}

interface SmartVotingConfig {
  enabled: boolean;
}

interface SystemSettingsState {
  smartAvailabilitySync: SmartAvailabilitySyncConfig;
  hostIntelligence: HostIntelligenceConfig;
  smartVoting: SmartVotingConfig;
}

export const SystemSettings = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState<SystemSettingsState>({
    smartAvailabilitySync: {
      enabled: false,
      max_connections_free: 3,
      max_connections_pro: 999,
    },
    hostIntelligence: {
      enabled: false,
    },
    smartVoting: {
      enabled: false,
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAllSettings();
  }, []);

  const loadAllSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['smart_availability_sync', 'host_intelligence_dashboard', 'smart_voting_features']);

      if (error) throw error;

      const newSettings = { ...settings };
      
      data?.forEach(setting => {
        const value = setting.setting_value as any;
        
        switch (setting.setting_key) {
          case 'smart_availability_sync':
            newSettings.smartAvailabilitySync = {
              enabled: value.enabled ?? false,
              max_connections_free: value.max_connections_free ?? 3,
              max_connections_pro: value.max_connections_pro ?? 999,
            };
            break;
          case 'host_intelligence_dashboard':
            newSettings.hostIntelligence = {
              enabled: value.enabled ?? false,
            };
            break;
          case 'smart_voting_features':
            newSettings.smartVoting = {
              enabled: value.enabled ?? false,
            };
            break;
        }
      });

      setSettings(newSettings);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast({
        title: "Error",
        description: "Failed to load system settings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveAllSettings = async () => {
    setSaving(true);
    try {
      const updates = [
        {
          setting_key: 'smart_availability_sync',
          setting_value: settings.smartAvailabilitySync,
        },
        {
          setting_key: 'host_intelligence_dashboard',
          setting_value: settings.hostIntelligence,
        },
        {
          setting_key: 'smart_voting_features',
          setting_value: settings.smartVoting,
        },
      ];

      for (const update of updates) {
        const { error } = await supabase
          .from('admin_settings')
          .update({ 
            setting_value: update.setting_value as any,
            updated_at: new Date().toISOString()
          })
          .eq('setting_key', update.setting_key);

        if (error) throw error;
      }

      toast({
        title: "Settings saved",
        description: "All system settings have been updated successfully.",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = <T extends keyof SystemSettingsState>(
    category: T,
    updates: Partial<SystemSettingsState[T]>
  ) => {
    setSettings(prev => ({
      ...prev,
      [category]: { ...prev[category], ...updates }
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              System Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">Loading settings...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Smart Availability Sync */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="w-5 h-5" />
            Smart Availability Sync
            <Badge variant={settings.smartAvailabilitySync.enabled ? "default" : "secondary"}>
              {settings.smartAvailabilitySync.enabled ? "Enabled" : "Disabled"}
            </Badge>
          </CardTitle>
          <CardDescription>
            Control calendar integration features that help guests sync their availability with event voting.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Feature Toggle */}
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                <Label htmlFor="availability-enabled" className="text-base font-medium">
                  Enable Smart Availability Sync
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Allow guests to connect their calendars and automatically check availability for event options.
              </p>
            </div>
            <Switch
              id="availability-enabled"
              checked={settings.smartAvailabilitySync.enabled}
              onCheckedChange={(enabled) => updateSetting('smartAvailabilitySync', { enabled })}
            />
          </div>

          {settings.smartAvailabilitySync.enabled && (
            <>
              {/* Free User Limits */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  <Label className="text-base font-medium">Free User Limits</Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="free-connections" className="text-sm">
                    Maximum calendar connections per event for free users
                  </Label>
                  <Input
                    id="free-connections"
                    type="number"
                    min="1"
                    max="50"
                    value={settings.smartAvailabilitySync.max_connections_free}
                    onChange={(e) => updateSetting('smartAvailabilitySync', { 
                      max_connections_free: parseInt(e.target.value) || 1 
                    })}
                    className="w-32"
                  />
                </div>
              </div>

              {/* Pro User Limits */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Crown className="w-4 h-4" />
                  <Label className="text-base font-medium">Pro User Limits</Label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pro-connections" className="text-sm">
                    Maximum calendar connections per event for Pro users
                  </Label>
                  <Input
                    id="pro-connections"
                    type="number"
                    min="1"
                    max="9999"
                    value={settings.smartAvailabilitySync.max_connections_pro}
                    onChange={(e) => updateSetting('smartAvailabilitySync', { 
                      max_connections_pro: parseInt(e.target.value) || 999 
                    })}
                    className="w-32"
                  />
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Host Intelligence Dashboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5" />
            Host Intelligence Dashboard
            <Badge variant={settings.hostIntelligence.enabled ? "default" : "secondary"}>
              {settings.hostIntelligence.enabled ? "Enabled" : "Disabled"}
            </Badge>
          </CardTitle>
          <CardDescription>
            AI-powered analytics and recommendations for event hosts including conflict matrix and smart suggestions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4" />
                <Label htmlFor="host-intelligence-enabled" className="text-base font-medium">
                  Enable Host Intelligence Dashboard
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Provide hosts with advanced analytics, conflict visualization, and AI-powered recommendations.
              </p>
            </div>
            <Switch
              id="host-intelligence-enabled"
              checked={settings.hostIntelligence.enabled}
              onCheckedChange={(enabled) => updateSetting('hostIntelligence', { enabled })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Smart Voting Features */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Smart Voting Features
            <Badge variant={settings.smartVoting.enabled ? "default" : "secondary"}>
              {settings.smartVoting.enabled ? "Enabled" : "Disabled"}
            </Badge>
          </CardTitle>
          <CardDescription>
            Intelligent voting assistance for guests including availability-based suggestions and group insights.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 border rounded-lg">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <Label htmlFor="smart-voting-enabled" className="text-base font-medium">
                  Enable Smart Voting Features
                </Label>
              </div>
              <p className="text-sm text-muted-foreground">
                Show availability assistant, group insights, and prevent voting on busy times for guests.
              </p>
            </div>
            <Switch
              id="smart-voting-enabled"
              checked={settings.smartVoting.enabled}
              onCheckedChange={(enabled) => updateSetting('smartVoting', { enabled })}
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t">
        <Button onClick={saveAllSettings} disabled={saving} size="lg">
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
};